import React, { useCallback, useMemo } from "react";

import { Attribute, MdmDeviceDto, MdmObjectDto } from "gql/generated/types";
import { useDisplayTimezone } from "hooks/useTimezone";
import { T } from "services/lang/LanguageService";
import { StringKey } from "services/lang/keys";
import { renderContentByAttributeType } from "../utils/utils";

type AdditionalInfoProps<T> = {
  attributes: (Attribute | null)[];
  objectState: T;
};

const AdditionalInfo = <T extends MdmObjectDto | Partial<MdmDeviceDto>>({
  attributes,
  objectState,
}: AdditionalInfoProps<T>) => {
  const displayTz = useDisplayTimezone();
  const attributeMap = useMemo(
    () => new Map(objectState.customFields?.map(field => [field?.key, field?.value])),
    [objectState.customFields]
  );

  const attributeGroups = useMemo(
    () =>
      [...attributes].reduce((acc, curr) => {
        const parent = curr?.parent?.toString();
        if (!parent || !curr) return acc;
        const parentAttributes = acc.get(parent);
        if (!parentAttributes) return acc.set(parent, [curr]);
        return acc.set(parent, [...parentAttributes, curr]);
      }, new Map<string, Attribute[]>()),
    [attributes]
  );

  const renderInput = useCallback(
    (body: Attribute) => {
      const value = attributeMap.get(body.id)?.value;
      return renderContentByAttributeType(body, value, displayTz);
    },
    [attributeMap, displayTz]
  );

  const nodes = useMemo(
    () =>
      [...attributeGroups].map(([key, value]) => (
        <table key={key} className="info-table">
          <caption>{key}</caption>
          <tbody>
            {value.map((attr: Attribute) => (
              <tr key={attr.id}>
                <th>{T(attr.title as StringKey, { defaultValue: attr.title })}</th>
                <td>{renderInput(attr)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )),
    [attributeGroups, renderInput]
  );

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{nodes}</>;
};

export default AdditionalInfo;